import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FaEnvelope, FaInfoCircle, FaSpinner, FaTimes, FaTrash, FaUserPlus } from "react-icons/fa";
import { DialogContext } from "../../store/context/DialogContext";
import {
  getExistingSpecialEmail,
  getSpecialEmails,
  hasUnsupportedMultipleSpecialEmails,
  doSpecialEmailsMatch,
  mergeApplicationFormUpdate,
  normalizeEmail,
  resolveApplicationFormId,
  validateInviteMember,
} from "./inviteMemberUtils";
import {
  removeSpecialEmail,
  saveSpecialEmail,
} from "./inviteMemberApi";
import ApplicationInviteSideModal from "./ApplicationInviteSideModal";
import "./styles.scss";

const getErrorMessage = (error, fallback = "Unable to update invited member access.") => {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return fallback;
};

const InviteAdditionalMemberDrawer = ({
  isOpen,
  onClose,
  formId,
  applicationForm,
  currentUserEmail,
  onApplicationFormUpdated,
  fetchInviteRecordApi,
  saveSpecialEmailApi = saveSpecialEmail,
  removeSpecialEmailApi = removeSpecialEmail,
}) => {
  const { showError, showMessage } = useContext(DialogContext) || {};
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loadingAction, setLoadingAction] = useState("");
  const [renderedApplicationForm, setRenderedApplicationForm] = useState(applicationForm || {});
  const hasLocalApplicationFormMutationRef = useRef(false);

  const incomingFormId = resolveApplicationFormId({ formId, applicationForm });
  const resolvedFormId = resolveApplicationFormId({ formId, applicationForm: renderedApplicationForm });
  const specialEmails = useMemo(() => getSpecialEmails(renderedApplicationForm), [renderedApplicationForm]);
  const existingSpecialEmail = getExistingSpecialEmail(renderedApplicationForm);
  const existingSpecialEmailAddress = existingSpecialEmail?.email || "";
  const hasExistingInvite = Boolean(existingSpecialEmailAddress);
  const hasUnsupportedInviteData = hasUnsupportedMultipleSpecialEmails(renderedApplicationForm);
  const isSaving = loadingAction === "save";
  const isRemoving = loadingAction === "remove";
  const isBusy = Boolean(loadingAction);
  const isInviteFormDisabled = isBusy || hasExistingInvite || hasUnsupportedInviteData;

  useEffect(() => {
    const nextApplicationForm = applicationForm || {};
    const currentFormId = resolveApplicationFormId({ formId, applicationForm: renderedApplicationForm });
    const nextFormId = resolveApplicationFormId({ formId, applicationForm: nextApplicationForm });
    const isSameForm = currentFormId === nextFormId;
    const parentMatchesLocalSpecialEmails = doSpecialEmailsMatch(
      nextApplicationForm?.specialEmails,
      renderedApplicationForm?.specialEmails
    );

    if (!isSameForm || !hasLocalApplicationFormMutationRef.current || parentMatchesLocalSpecialEmails) {
      setRenderedApplicationForm(nextApplicationForm);
      hasLocalApplicationFormMutationRef.current = false;
    }
  }, [applicationForm, formId, renderedApplicationForm]);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setEmail("");
      setError("");
      setLoadingAction("");
    }
  }, [incomingFormId, isOpen]);

  useEffect(() => {
    if (!isOpen || typeof fetchInviteRecordApi !== "function") {
      return undefined;
    }

    let isActive = true;
    setLoadingAction((currentAction) => currentAction || "fetch");

    fetchInviteRecordApi()
      .then((latestInviteRecord) => {
        if (!isActive || !latestInviteRecord) {
          return;
        }

        hasLocalApplicationFormMutationRef.current = true;
        setRenderedApplicationForm((previousApplicationForm) => (
          mergeApplicationFormUpdate(previousApplicationForm, latestInviteRecord)
        ));
      })
      .catch((apiError) => {
        if (isActive) {
          const message = getErrorMessage(apiError);
          setError(message);
          showError?.(apiError);
        }
      })
      .finally(() => {
        if (isActive) {
          setLoadingAction((currentAction) => (currentAction === "fetch" ? "" : currentAction));
        }
      });

    return () => {
      isActive = false;
    };
  }, [fetchInviteRecordApi, incomingFormId, isOpen, showError]);

  const handleClose = () => {
    if (isBusy) {
      return;
    }

    onClose?.();
  };

  const handleApiError = (apiError) => {
    const message = getErrorMessage(apiError);
    setError(message);
    showError?.(apiError);
  };

  const handleInviteSubmit = async (event) => {
    event?.preventDefault?.();
    const validationError = validateInviteMember({
      name,
      email,
      currentUserEmail,
      applicationForm: renderedApplicationForm,
      existingSpecialEmail,
    });

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!resolvedFormId) {
      setError("Application form id is required.");
      return;
    }

    setError("");
    setLoadingAction("save");

    try {
      const updatedApplicationForm = await saveSpecialEmailApi({
        formId: resolvedFormId,
        name,
        email: normalizeEmail(email),
      });
      hasLocalApplicationFormMutationRef.current = true;
      setRenderedApplicationForm((previousApplicationForm) => (
        mergeApplicationFormUpdate(previousApplicationForm, updatedApplicationForm)
      ));
      onApplicationFormUpdated?.(updatedApplicationForm);
      showMessage?.("Invitation access updated.");
      setName("");
      setEmail("");
    } catch (apiError) {
      handleApiError(apiError);
    } finally {
      setLoadingAction("");
    }
  };

  const handleRemoveInvite = async () => {
    if (!resolvedFormId || isBusy) {
      return;
    }

    setError("");
    setLoadingAction("remove");

    try {
      const updatedApplicationForm = await removeSpecialEmailApi({ formId: resolvedFormId });
      hasLocalApplicationFormMutationRef.current = true;
      setRenderedApplicationForm((previousApplicationForm) => (
        mergeApplicationFormUpdate(previousApplicationForm, updatedApplicationForm)
      ));
      onApplicationFormUpdated?.(updatedApplicationForm);
      showMessage?.("Invited member access removed.");
    } catch (apiError) {
      handleApiError(apiError);
    } finally {
      setLoadingAction("");
    }
  };

  const renderConcurrentAccessNote = () => (
    <div className="invite-member-drawer__info-note" data-testid="invite-member-concurrent-access-note">
      <FaInfoCircle aria-hidden="true" />
      <span>
        Only one person can work on this application at a time. If someone sees a login error,
        ask the other user to log out of any open session before trying again.
      </span>
    </div>
  );

  return (
    <ApplicationInviteSideModal
      isOpen={isOpen}
      onClose={handleClose}
      closeDisabled={isBusy}
      titleId="invite-member-title"
      panelClassName="invite-member-drawer"
      panelTestId="invite-member-drawer"
      dataRecordId={resolvedFormId}
      initialFocusSelector='[data-testid="invite-member-name--input"]'
    >
      <div className="invite-member-drawer__header">
        <div className="invite-member-drawer__title-wrap">
          <span className="invite-member-drawer__icon" aria-hidden="true">
            <FaUserPlus />
          </span>
          <div>
            <h2 className="invite-member-drawer__title" id="invite-member-title">Invite Additional Member</h2>
            <p className="invite-member-drawer__subtitle">
              Invite one person to help complete this application.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="invite-member-drawer__close"
          onClick={handleClose}
          disabled={isBusy}
          aria-label="Close invite additional member"
          data-testid="invite-member-close"
        >
          <FaTimes />
        </button>
      </div>

      <div className="invite-member-drawer__body">
        {hasUnsupportedInviteData ? (
          <div className="invite-member-drawer__notice" data-testid="invite-member-error">
            This application has {specialEmails.length} invited members. Only one invited member is supported.
            Remove extra access from backend data before inviting another member.
          </div>
        ) : null}

        {hasExistingInvite ? (
          <div className="invite-member-drawer__existing" data-testid="invite-member-existing-card">
            <div>
              <div className="invite-member-drawer__eyebrow">Invited member</div>
              {existingSpecialEmail?.name ? (
                <div className="invite-member-drawer__existing-name">
                  {existingSpecialEmail.name}
                </div>
              ) : null}
              <div className="invite-member-drawer__existing-email">
                <FaEnvelope aria-hidden="true" />
                <span>{existingSpecialEmailAddress}</span>
              </div>
            </div>
            <button
              type="button"
              className="invite-member-drawer__remove"
              onClick={handleRemoveInvite}
              disabled={isBusy}
              data-testid="invite-member-remove"
            >
              {isRemoving ? <FaSpinner className="invite-member-drawer__spinner" /> : <FaTrash />}
              <span>{isRemoving ? "Removing" : "Remove"}</span>
            </button>
          </div>
        ) : null}

        {hasExistingInvite && !hasUnsupportedInviteData ? renderConcurrentAccessNote() : null}

        {!hasExistingInvite && !hasUnsupportedInviteData ? (
          <form className="invite-member-drawer__form" onSubmit={handleInviteSubmit}>
            <label className="invite-member-drawer__field" data-testid="invite-member-name">
              <span>Name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={isInviteFormDisabled}
                placeholder="Member name"
                data-testid="invite-member-name--input"
                autoComplete="name"
              />
            </label>

            <label className="invite-member-drawer__field" data-testid="invite-member-email">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isInviteFormDisabled}
                placeholder="member@example.com"
                data-testid="invite-member-email--input"
                autoComplete="email"
              />
            </label>

            {renderConcurrentAccessNote()}

            {error ? (
              <div className="invite-member-drawer__error" data-testid="invite-member-error">
                {error}
              </div>
            ) : null}

            <div className="invite-member-drawer__footer">
              <button
                type="button"
                className="invite-member-drawer__secondary"
                onClick={handleClose}
                disabled={isBusy}
                data-testid="invite-member-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="invite-member-drawer__primary"
                disabled={isInviteFormDisabled}
                data-testid="invite-member-submit"
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="invite-member-drawer__spinner" data-testid="invite-member-loading" />
                    <span>Sending</span>
                  </>
                ) : (
                  <>
                    <FaUserPlus />
                    <span>Send invite</span>
                  </>
                )}
              </button>
            </div>
          </form>
        ) : null}

        {(hasExistingInvite || hasUnsupportedInviteData) && error ? (
          <div className="invite-member-drawer__error" data-testid="invite-member-error">
            {error}
          </div>
        ) : null}
      </div>
    </ApplicationInviteSideModal>
  );
};

export default InviteAdditionalMemberDrawer;
